import {
  CommonModule,
  isPlatformBrowser,
  NgClass,
  NgIf,
} from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SwiperOptions } from 'swiper/types';
import { SwiperContainer } from 'swiper/element';
import { ApiService } from '../../../services/api.service';
import { CardChipsComponent } from '../card/card-chips/card-chips.component';
import { SectionContainerComponent } from '../section-container/section-container.component';
import { SectionDescriptionComponent } from '../section-description/section-description.component';
import { SectionHeadingComponent } from '../section-heading/section-heading.component';
import { ArtifactCardComponent } from '../../utility/artifact-card/artifact-card.component';
import { CardSkeletonLoaderComponent } from '../../utility/card-skeleton-loader/card-skeleton-loader.component';
import { CategoryTypes } from '../../../models/category-types.model';
import { Dataset } from '../../../models/dataset.model';
import { routes } from '../../../constants/routes';
import { CouldntLoadDataComponent } from '../couldnt-load-data/couldnt-load-data.component';
import { NoRecordFoundComponent } from '../no-record-found/no-record-found.component';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';
import { ToasterService } from '../../../services/toaster.service';
import { TimeRange } from '../../../models/filter.model';
import moment from 'moment';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-home-dataset',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    SectionContainerComponent,
    SectionHeadingComponent,
    SectionDescriptionComponent,
    ArtifactCardComponent,
    CardChipsComponent,
    NgClass,
    NgIf,
    CardSkeletonLoaderComponent,
    CouldntLoadDataComponent,
    NoRecordFoundComponent,
  ],
  templateUrl: './home-dataset.component.html',
  styleUrl: './home-dataset.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeDatasetComponent
  implements OnInit, OnDestroy, ApiErrorHandler
{
  datasets?: Dataset[];
  selectedCategory!: CategoryTypes;
  isDatasetsLoading!: boolean;
  isSwiperInitialized = false;
  private fetchDatasetsSubs!: Subscription;
  readonly CategoryTypes = CategoryTypes;
  readonly TimeRange = TimeRange;
  readonly routes = routes;
  readonly datasetDetails = {
    name: 'Datasets',
    description: 'Search and access datasets for your AI projects',
  };
  isErrorFetchingDatasets: boolean = false;
  selectedTimeRange: TimeRange = TimeRange.week;
  filterParams = new HttpParams();

  @ViewChild('swiperContainerDataset')
  private swiperContainer!: ElementRef<any>;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    public toaster: ToasterService,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    this.selectedCategory = this.CategoryTypes.trending;
    this.selectedTimeRange = this.TimeRange.week;
    this.fetchDatasets(this.selectedCategory);
  }

  public fetchDatasets(filterType: string) {
    this.datasets = [];
    this.isDatasetsLoading = true;
    // this.getTimeRange();

    let fetchSelectedCategoryDataset$: Observable<any>;
    if (filterType === this.CategoryTypes.new) {
      fetchSelectedCategoryDataset$ = this.apiService.fetchDatasets(
        1,
        this.filterParams
      );
    } else {
      fetchSelectedCategoryDataset$ = this.apiService.fetchFilteredDataset(
        this.selectedCategory,
        this.filterParams
      );
    }

    this.fetchDatasetsSubs = fetchSelectedCategoryDataset$
      .pipe(
        finalize(() => {
          this.isDatasetsLoading = false;
        })
      )
      .subscribe({
        next: (resp) => {
          if (filterType === this.CategoryTypes.new) {
            this.datasets = resp.data.cards.data;
          } else {
            this.datasets = resp;
          }

          if (
            this.datasets?.length &&
            !this.isSwiperInitialized &&
            isPlatformBrowser(this.platformId)
          ) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          } else {
            this.swiperContainer.nativeElement?.swiper?.update();
          }
        },
        error: (error) => {
          this.isErrorFetchingDatasets = true;
          this.datasets = [];
          apiErrorHandler.call(this, error);
        },
      });
  }

  private initSwiperCarousel() {
    this.isSwiperInitialized = true;
    const swiperProps: SwiperOptions = {
      slidesPerView: 1.2,
      speed: 500,
      spaceBetween: 18,
      navigation: {
        prevEl: '.hero-carousel-arrow--prev',
        nextEl: '.hero-carousel-arrow--next',
      },
      loop: true,
      autoplay: {
        delay: 10000,
        stopOnLastSlide: false,
      },
      pagination: {
        el: '.dataset-swiper-pagination ',
        clickable: true,
      },
      breakpoints: {
        1300: {
          slidesPerView: 4,
          spaceBetween: 18,
          // enabled: this.datasetDetails?.cards?.length > 4,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.datasets!.length < 4) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
        1000: {
          slidesPerView: 3,
          spaceBetween: 18,
          // enabled: this.datasetDetails?.cards?.length > 3,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.datasets!.length < 3) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 18,
          // enabled: this.datasetDetails?.cards?.length > 2,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.datasets!.length < 2) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
      },
    };

    Object.assign(this.swiperContainer.nativeElement, swiperProps);
    (<SwiperContainer>this.swiperContainer.nativeElement).initialize();
  }

  getTimeRange() {
    if (this.selectedTimeRange === TimeRange.week) {
      return (this.filterParams = new HttpParams().set(
        'dateRange',
        JSON.stringify({
          startDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
          endDate: moment().endOf('day').format('YYYY-MM-DD'),
        })
      ));
    } else if (this.selectedTimeRange === TimeRange.month) {
      return (this.filterParams = new HttpParams().set(
        'dateRange',
        JSON.stringify({
          startDate: moment().startOf('month').format('YYYY-MM-DD'),
          endDate: moment().endOf('day').format('YYYY-MM-DD'),
        })
      ));
    }
    return this.filterParams;
  }

  ngOnDestroy() {
    this.fetchDatasetsSubs.unsubscribe();
  }
}
